import React, { useState, useRef, useEffect } from 'react';
import {
  AppBar, Toolbar, Button, IconButton, Badge,
  BottomNavigation, BottomNavigationAction, Box, Divider, List,
  ListItem, ListItemText, ListItemIcon, CircularProgress,
  Paper, ClickAwayListener, Popper, Typography, Switch,
  FormControlLabel, Snackbar, Alert, useMediaQuery
} from '@mui/material';
import {
  ShoppingCart, AccountCircle, Home, Wallet, ShoppingBag, Dashboard,
  AdminPanelSettings, Notifications as NotificationsIcon,
  NotificationsNone, DoneAll, Close, Logout, VolumeUp, VolumeOff, HeadsetMic,
  Share as ShareIcon,
  Storefront as StorefrontIcon
} from '@mui/icons-material';
import PhoneIcon from '@mui/icons-material/Phone';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useNotifications } from '../context/NotificationContext';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { useTheme } from '@mui/material/styles';

const NavBar = ({ keywordsData, language, toggleLanguage }) => {
  const { cartCount, setCartOpen } = useCart();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isRtl = theme.direction === 'rtl';
  const { user, logout } = useAuth();
  const {
    notifications,
    unreadCount,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    loading,
    soundEnabled,
    toggleSound
  } = useNotifications();

  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [pharmacyStatus, setPharmacyStatus] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [tableScan, setTableScan] = useState(Number(localStorage.getItem('tableId')) || 0);
  const notificationRef = useRef(null);

  const getCurrentNavigationValue = () => {
    const path = location.pathname;
    const restaurantId = localStorage.getItem('current_rest_id_now') || 0;
    if (path === '/' || path === '/home') return 'home';
    if (path === '/account') return 'account';
    if (path === '/wallet') return 'wallet';
    if (path === '/admin') return 'admin';
    if (path === '/super') return 'super';
    if (path === '/about') return 'about';
    if (path === `/restaurant/${parseInt(restaurantId)}`) return `restaurant/${parseInt(restaurantId)}`;
    if (path === '/casher/orders') return 'casher/orders';
    if (path === '/admin/chats') return 'admin/chats';
    if (path === '/super/chats') return 'super/chats';
    return '';
  };

  const [value, setValue] = useState(getCurrentNavigationValue());

  useEffect(() => {
    setValue(getCurrentNavigationValue());
  }, [location.pathname]);

const getBottomNavItems = () => {
  const restaurantId = localStorage.getItem('current_rest_id_now');
  const baseItems = [];
  
  // Check if restaurantId exists and user is not admin or super
  const hasValidRestaurantId = restaurantId && restaurantId !== '0' && restaurantId !== 0;
  const isNotAdminOrSuper = user?.role !== 'admin' && user?.role !== 'super';
  
  if (hasValidRestaurantId && isNotAdminOrSuper) { 
    baseItems.push({ 
      label: language === 'ar' ? 'المنيو' : 'Menu', 
      value: `restaurant/${parseInt(restaurantId)}`, 
      icon: <StorefrontIcon /> 
    });
  }
  
  baseItems.push(
    { label: language === 'ar' ? keywordsData.home_txt_ar : keywordsData.home_txt_en, value: 'home', icon: <Home /> },
    { label: language === 'ar' ? keywordsData.account_txt_ar : keywordsData.account_txt_en, value: 'account', icon: <AccountCircle /> },
    { label: language === 'ar' ? keywordsData.wallet_txt_ar : keywordsData.wallet_txt_en, value: 'wallet', icon: <Wallet /> },
  );

  const roleBasedItems = {
    super: [
      ...baseItems,
      { label: language === 'ar' ? 'المحادثات' : 'Chats', value: 'super/chats', icon: <HeadsetMic /> },
      { label: language === 'ar' ? keywordsData.dashboard_txt_ar : keywordsData.dashboard_txt_en, value: 'super', icon: <Dashboard /> },
    ],
    admin: [
      ...baseItems,
      { label: language === 'ar' ? 'المحادثات' : 'Chats', value: 'admin/chats', icon: <HeadsetMic /> },
      { label: language === 'ar' ? keywordsData.dashboard_txt_ar : keywordsData.dashboard_txt_en, value: 'admin', icon: <Dashboard /> },
    ],
    casher: [
      ...baseItems,
      { label: language === 'ar' ? 'الطلبات' : 'Orders', value: 'casher/orders', icon: <ShoppingBag /> },
    ],
    user: [
      ...baseItems,
      { label: language === 'ar' ? keywordsData.contact_txt_ar : keywordsData.contact_txt_en, value: 'about', icon: <PhoneIcon /> },
    ],
  };

  return roleBasedItems[user?.role] || roleBasedItems.user;
};

  useEffect(() => {
    const fetchPharmacyStatus = async () => {
      try {
        const response = await api.get('/categories/pharm_status');
        setPharmacyStatus(response.data.value === '1');
      } catch (error) {
        console.error('Error fetching pharmacy status:', error);
        const savedStatus = localStorage.getItem('pharmacyStatus');
        if (savedStatus !== null) {
          setPharmacyStatus(savedStatus === 'true');
        }
      }
    };
    if (user?.role === 'admin') fetchPharmacyStatus();
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNotificationClick = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
    fetchNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const openStatusTxt = language === 'ar' ? keywordsData.open_caffe_status_txt_ar : keywordsData.open_caffe_status_txt_en;
  const closedStatusTxt = language === 'ar' ? keywordsData.closed_caffe_status_txt_ar : keywordsData.closed_caffe_status_txt_en;
  const openTxt = language === 'ar' ? keywordsData.open_status_txt_ar : keywordsData.open_status_txt_en;
  const closedTxt = language === 'ar' ? keywordsData.closed_status_txt_ar : keywordsData.closed_status_txt_en;

  const handleStatusChange = async (event) => {
    const newStatus = event.target.checked;
    setLoadingStatus(true);

    try {
      const response = await api.put('/admin/pharm_status', {
        key: 'pharm_open_status',
        value: newStatus ? '1' : '0'
      });

      setPharmacyStatus(newStatus);
      localStorage.setItem('pharmacyStatus', newStatus);

      setSnackbar({
        open: true,
        message: newStatus ? openStatusTxt : closedStatusTxt,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating pharmacy status:', error);
      setSnackbar({
        open: true,
        message: 'فشل في تحديث حالة المتجر',
        severity: 'error'
      });
      setPharmacyStatus(!newStatus);
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const [isCopied, setIsCopied] = useState(false);
  const currentUrl = window.location.href;
  const siteTitle = document.title;

  const shareWithWebAPI = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: siteTitle,
          text: `تابع أحدث العروض على ${siteTitle}`,
          url: currentUrl,
        });
        console.log('Shared successfully');
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      console.log('Web Share API not supported');
      copyToClipboard();
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(currentUrl).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }).catch(err => {
      console.error('Failed to copy: ', err);
      const textArea = document.createElement('textarea');
      textArea.value = currentUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    });
  };

  const open = Boolean(anchorEl);
  const id = open ? 'notification-popper' : undefined;

  return (
    <>
      {/* Modern App Bar */}
      <AppBar
        position="sticky"
        sx={{
          top: 0,
          background: '#0D0D0D',
          borderBottom: '2px solid #FFD700',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          zIndex: 1000,
          transition: 'all 0.3s ease',
        }}
      >
        <Toolbar sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          minHeight: { xs: '56px', sm: '64px' },
          px: { xs: 1.5, sm: 2, md: 3 }
        }}>
          {/* Logo */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              height: { xs: "45px", sm: "55px", md: "65px" },
              width: { xs: "65px", sm: "80px", md: "95px" },
              transition: 'all 0.3s ease',
              "&:hover": {
                transform: "scale(1.05)",
              }
            }}
            onClick={() => navigate("/")}
          >
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 120 120"
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <linearGradient id="mainGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#FFD700" />
                  <stop offset="100%" stopColor="#FFA000" />
                </linearGradient>
                <radialGradient id="centerGlow" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#FFD700" stopOpacity="0.15" />
                  <stop offset="100%" stopColor="#FFD700" stopOpacity="0" />
                </radialGradient>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="2" result="blur"/>
                  <feMerge>
                    <feMergeNode in="blur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>

              <circle cx="60" cy="60" r="55" fill="none" stroke="url(#mainGradient)" strokeWidth="2.5" filter="url(#glow)" opacity="0.8"/>
              <circle cx="60" cy="60" r="50" fill="none" stroke="#FFD700" strokeWidth="1" strokeDasharray="4 6" opacity="0.3"/>
              <circle cx="60" cy="60" r="45" fill="#0D0D0D" />
              <circle cx="60" cy="60" r="45" fill="url(#centerGlow)" />
              
              <g transform="translate(48, 32)">
                <rect x="0" y="0" width="6" height="6" fill="#FFD700" rx="1" />
                <rect x="18" y="0" width="6" height="6" fill="#FFD700" rx="1" />
                <rect x="0" y="18" width="6" height="6" fill="#FFD700" rx="1" />
                <rect x="6" y="6" width="6" height="6" fill="#FFD700" rx="1" />
                <rect x="12" y="12" width="6" height="6" fill="#FFD700" rx="1" />
                <rect x="18" y="18" width="6" height="6" fill="#FFD700" rx="1" />
              </g>

              <text x="60" y="75" textAnchor="middle" fontSize="18" fontWeight="900" fill="#FFD700" fontFamily="Arial, sans-serif" letterSpacing="2">QR</text>
              <text x="60" y="92" textAnchor="middle" fontSize="10" fontWeight="700" fill="#FFA500" fontFamily="Arial, sans-serif" letterSpacing="2.5" opacity="0.9">TABLE</text>
            </svg>
          </Box>

          {/* Right Side Buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1, md: 1.5 } }}>
            {/* Cart Button */}
            <IconButton 
              onClick={() => setCartOpen(true)}
              sx={{
                color: '#FFFFFF',
                transition: 'all 0.2s ease',
                '&:hover': {
                  color: '#FFD700',
                  backgroundColor: 'rgba(255, 215, 0, 0.1)',
                  transform: 'scale(1.1)',
                }
              }}
            >
              <Badge 
                badgeContent={cartCount} 
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: '#FFD700',
                    color: '#000000',
                    fontWeight: 'bold',
                    fontSize: '0.7rem',
                    height: '18px',
                    minWidth: '18px',
                  }
                }}
              >
                <ShoppingCart />
              </Badge>
            </IconButton>

            {/* Share Button - Show on mobile only */}
            {(isMobile || isTablet) && (
              <IconButton
                onClick={shareWithWebAPI}
                sx={{
                  color: '#FFFFFF',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    color: '#FFD700',
                    backgroundColor: 'rgba(255, 215, 0, 0.1)',
                    transform: 'scale(1.1)',
                  }
                }}
              >
                <ShareIcon />
              </IconButton>
            )}

            {user ? (
              <>
                {/* Pharmacy Status Toggle (Admin Only) - Hide text on mobile */}
                {user.role === 'admin' && !isMobile && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 0.5 }}>
                    {loadingStatus ? (
                      <CircularProgress size={20} sx={{ color: '#FFD700' }} />
                    ) : (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={pharmacyStatus}
                            onChange={handleStatusChange}
                            disabled={loadingStatus}
                            size="small"
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: '#FFD700',
                                '&:hover': { backgroundColor: 'rgba(255, 215, 0, 0.08)' },
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: '#FFD700',
                              },
                              '& .MuiSwitch-track': { backgroundColor: '#2D2D2D' },
                            }}
                          />
                        }
                        label={
                          <Typography variant="caption" sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '0.7rem' }}>
                            {pharmacyStatus ? openTxt : closedTxt}
                          </Typography>
                        }
                        labelPlacement="start"
                        sx={{ mr: 0 }}
                      />
                    )}
                  </Box>
                )}
                
                {/* Notifications Button */}
                <IconButton
                  ref={notificationRef}
                  onClick={handleNotificationClick}
                  sx={{
                    color: '#FFFFFF',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      color: '#FFD700',
                      backgroundColor: 'rgba(255, 215, 0, 0.1)',
                      transform: 'scale(1.1)',
                    }
                  }}
                >
                  <Badge 
                    badgeContent={unreadCount}
                    sx={{
                      '& .MuiBadge-badge': {
                        backgroundColor: '#FFD700',
                        color: '#000000',
                        fontWeight: 'bold',
                        fontSize: '0.7rem',
                        height: '18px',
                        minWidth: '18px',
                      }
                    }}
                  >
                    {unreadCount > 0 ? <NotificationsIcon /> : <NotificationsNone />}
                  </Badge>
                </IconButton>
                
                {/* Language Toggle */}
                <Button 
                  onClick={toggleLanguage}
                  sx={{
                    color: '#FFFFFF',
                    minWidth: 'auto',
                    fontWeight: 700,
                    fontSize: { xs: '0.75rem', sm: '0.85rem' },
                    px: { xs: 1, sm: 1.5 },
                    '&:hover': {
                      color: '#FFD700',
                      backgroundColor: 'rgba(255, 215, 0, 0.1)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  {language === 'ar' ? 'EN' : 'AR'}
                </Button>
                
                {/* Logout Button */}
                <IconButton
                  onClick={handleLogout}
                  sx={{
                    color: '#FFFFFF',
                    '&:hover': {
                      color: '#FFD700',
                      backgroundColor: 'rgba(255, 215, 0, 0.1)',
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Logout />
                </IconButton>
              </>
            ) : (
              <>
                <Button 
                  component={Link} 
                  to="/login"
                  sx={{
                    color: '#FFFFFF',
                    fontWeight: 600,
                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                    px: { xs: 1, sm: 1.5 },
                    '&:hover': {
                      color: '#FFD700',
                      backgroundColor: 'rgba(255, 215, 0, 0.1)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  {language === 'ar' ? keywordsData.login_ar : keywordsData.login_en}
                </Button>
                <Button 
                  component={Link} 
                  to="/register"
                  sx={{
                    color: '#FFFFFF',
                    fontWeight: 600,
                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                    px: { xs: 1, sm: 1.5 },
                    '&:hover': {
                      color: '#FFD700',
                      backgroundColor: 'rgba(255, 215, 0, 0.1)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  {language === 'ar' ? keywordsData.register_ar : keywordsData.register_en}
                </Button>
                <Button 
                  onClick={toggleLanguage}
                  sx={{
                    color: '#FFFFFF',
                    minWidth: 'auto',
                    fontWeight: 700,
                    fontSize: { xs: '0.75rem', sm: '0.85rem' },
                    px: { xs: 1, sm: 1.5 },
                    '&:hover': {
                      color: '#FFD700',
                      backgroundColor: 'rgba(255, 215, 0, 0.1)',
                    },
                  }}
                >
                  {language === 'ar' ? 'EN' : 'AR'}
                </Button>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Notifications Dropdown */}
      <Popper
        id={id}
        dir={language === 'ar' ? 'rtl' : 'ltr'}
        open={open}
        anchorEl={anchorEl}
        placement="bottom-end"
        sx={{ zIndex: 1400 }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            sx={{
              width: { xs: '90vw', sm: 380 },
              maxHeight: 450,
              overflow: 'auto',
              boxShadow: '0 8px 32px rgba(255, 215, 0, 0.2)',
              backgroundColor: '#0D0D0D',
              border: '1px solid #FFD700',
              borderRadius: '12px',
              mt: 1,
            }}
          >
            <Box sx={{ 
              p: 2, 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              borderBottom: '1px solid rgba(255, 215, 0, 0.2)',
              background: 'linear-gradient(135deg, #1A1A1A 0%, #0D0D0D 100%)',
              borderRadius: '12px 12px 0 0',
            }}>
              <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 700, fontSize: '1rem' }}>
                {language === 'ar' ? keywordsData.noty_title_ar : keywordsData.noty_title_en}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton 
                  size="small" 
                  onClick={markAllAsRead} 
                  disabled={unreadCount === 0}
                  sx={{
                    color: '#B3B3B3',
                    '&:hover': {
                      color: '#FFD700',
                      backgroundColor: 'rgba(255, 215, 0, 0.1)',
                    },
                  }}
                >
                  <DoneAll fontSize="small" />
                </IconButton>
                <IconButton 
                  size="small" 
                  onClick={toggleSound} 
                  sx={{ 
                    color: soundEnabled ? '#FFD700' : '#B3B3B3',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 215, 0, 0.1)',
                    }
                  }}
                >
                  {soundEnabled ? <VolumeUp fontSize="small" /> : <VolumeOff fontSize="small" />}
                </IconButton>
                <IconButton 
                  size="small" 
                  onClick={handleClose}
                  sx={{
                    color: '#B3B3B3',
                    '&:hover': {
                      color: '#FFD700',
                      backgroundColor: 'rgba(255, 215, 0, 0.1)',
                    }
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {loading ? (
              <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={32} sx={{ color: '#FFD700' }} />
              </Box>
            ) : notifications.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography sx={{ color: '#B3B3B3' }}>
                  {language === 'ar' ? keywordsData.noty_no_record_ar : keywordsData.noty_no_record_en}
                </Typography>
              </Box>
            ) : (
              <List dense sx={{ p: 0 }}>
                {notifications.map((notification) => (
                  <ListItem
                    key={notification.id}
                    sx={{
                      backgroundColor: notification.read ? 'transparent' : 'rgba(255, 215, 0, 0.08)',
                      cursor: 'pointer',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 215, 0, 0.15)',
                      },
                      py: 1.5
                    }}
                    onClick={() => {
                      markAsRead(notification.id);
                      if (notification.link) {
                        navigate(notification.link);
                      }
                      handleClose();
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {notification.icon === 'order' && <ShoppingCart fontSize="small" sx={{ color: '#FFD700' }} />}
                      {notification.icon === 'system' && <AdminPanelSettings fontSize="small" sx={{ color: '#FFD700' }} />}
                    </ListItemIcon>
                    <ListItemText
                      primary={notification.title}
                      secondary={notification.message}
                      primaryTypographyProps={{
                        fontWeight: notification.read ? 'normal' : 'bold',
                        color: notification.read ? '#B3B3B3' : '#FFFFFF',
                        fontSize: '0.85rem'
                      }}
                      secondaryTypographyProps={{
                        color: '#6B6B6B',
                        fontSize: '0.75rem'
                      }}
                    />
                    <Typography variant="caption" sx={{ color: '#6B6B6B', minWidth: 55, textAlign: 'right', fontSize: '0.7rem' }}>
                      {new Date(notification.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>

      {/* Bottom Navigation - SHOW ON ALL DEVICES (Mobile, Tablet, AND Desktop) */}
      <BottomNavigation
        value={value}
        onChange={(event, newValue) => {
          setValue(newValue);
          navigate(`/${newValue === 'home' ? '' : newValue}`);
        }}
        showLabels
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          background: '#0D0D0D',
          borderTop: '2px solid #FFD700',
          height: { xs: 56, sm: 60, md: 65 },
          boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.3)',
        }}
      >
        {getBottomNavItems().map((item) => (
          <BottomNavigationAction 
            key={item.value}
            label={item.label}
            value={item.value}
            icon={item.icon}
            sx={{
              color: '#6B6B6B',
              transition: 'all 0.2s ease',
              '& .MuiBottomNavigationAction-label': {
                fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                fontWeight: 500,
              },
              '& .MuiSvgIcon-root': {
                fontSize: { xs: '1.4rem', sm: '1.5rem', md: '1.6rem' },
              },
              '&.Mui-selected': {
                color: '#FFD700',
                '& .MuiBottomNavigationAction-label': {
                  fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
                  fontWeight: 800,
                },
              },
            }} 
          />
        ))}
      </BottomNavigation>

      {/* Add padding bottom to prevent content from hiding under bottom nav on all devices */}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{
            backgroundColor: '#0D0D0D',
            color: '#FFFFFF',
            border: '1px solid #FFD700',
            borderRadius: '12px',
            fontWeight: 600,
            '& .MuiAlert-icon': {
              color: snackbar.severity === 'success' ? '#FFD700' : '#FF3D00',
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NavBar;